import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Plus } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const de = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const eur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const errMsg = async (e) => {
  if (e.response?.data instanceof Blob) {
    try { return JSON.parse(await e.response.data.text())?.detail || e.message; } catch { return e.message; }
  }
  return formatApiErrorDetail(e.response?.data?.detail) || e.message;
};
export const openPdf = async (url, name) => {
  try {
    const r = await api.get(url, { responseType: "blob", timeout: 60000 });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = name;
    a.click();
  } catch (e) { toast.error(await errMsg(e)); }
};

export function WbAkteMassnahme({ detail }) {
  const p = detail.participant, c = detail.class;
  const total = detail.modules.reduce((s, m) => s + Number(m.ue || 0) * Number(m.price_per_ue || 0), 0);
  const ue = detail.modules.reduce((s, m) => s + Number(m.ue || 0), 0);
  return (
    <div className="space-y-4" data-testid="wb-akte-massnahme">
      <div className="grid sm:grid-cols-3 gap-3">
        {[["Weiterbildung", detail.measure?.name || "—"], ["Klasse", c?.name || "nicht zugewiesen"], ["Zeitraum", `${de(p.start_date || c?.start_date)} – ${de(p.end_date || c?.end_date)}`]].map(([l, v]) => (
          <div key={l} className="border border-slate-200 bg-white p-4"><div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">{l}</div><div className="text-sm text-slate-900 mt-1">{v}</div></div>
        ))}
      </div>
      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[480px]">
          <thead className="text-[10px] uppercase tracking-overline text-slate-500 border-b"><tr><th className="text-left px-4 py-2">Gebuchtes Modul</th><th className="text-right px-4 py-2">UE</th><th className="text-right px-4 py-2">€ / UE</th><th className="text-right px-4 py-2">Kosten</th></tr></thead>
          <tbody>
            {detail.modules.map((m) => (
              <tr key={m.module_id} className="border-b"><td className="px-4 py-2">{m.name}</td><td className="px-4 py-2 text-right">{m.ue}</td><td className="px-4 py-2 text-right">{eur(m.price_per_ue)}</td><td className="px-4 py-2 text-right">{eur(Number(m.ue || 0) * Number(m.price_per_ue || 0))}</td></tr>
            ))}
            <tr className="font-medium"><td className="px-4 py-2">Summe</td><td className="px-4 py-2 text-right">{ue}</td><td /><td className="px-4 py-2 text-right" data-testid="wb-akte-total">{eur(total)}</td></tr>
          </tbody>
        </table>
        {!detail.modules.length && <div className="p-4 text-sm text-slate-500">Keine Module gebucht.</div>}
      </div>
    </div>
  );
}

function monthsBetween(a, b) {
  if (!a) return [];
  const out = [];
  const d = new Date(`${a.slice(0, 7)}-01`), end = new Date(`${(b || new Date().toISOString()).slice(0, 7)}-01`);
  const stop = new Date(Math.min(end, new Date()));
  while (d <= stop && out.length < 36) { out.push(d.toISOString().slice(0, 7)); d.setMonth(d.getMonth() + 1); }
  return out.reverse();
}

export function WbAkteAnwesenheit({ detail }) {
  const p = detail.participant, c = detail.class;
  const months = monthsBetween(p.start_date || c?.start_date, p.end_date || c?.end_date);
  if (!months.length) return <div className="text-sm text-slate-500" data-testid="wb-akte-anwesenheit">Noch kein Teilnahmezeitraum hinterlegt.</div>;
  return (
    <div className="border border-slate-200 bg-white divide-y" data-testid="wb-akte-anwesenheit">
      {months.map((m) => (
        <div key={m} className="flex items-center justify-between px-4 py-3 text-sm">
          <span>{new Date(`${m}-01`).toLocaleDateString("de-DE", { month: "long", year: "numeric" })}</span>
          <button onClick={() => openPdf(`/weiterbildung/participants/${p.participant_id}/monthly-pdf/${m}`, `Anwesenheit_${m}.pdf`)} className="inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50" data-testid={`wb-akte-month-${m}`}>
            <Download size={12} /> Monatsliste PDF
          </button>
        </div>
      ))}
    </div>
  );
}

const KINDS = { contacted: "Kontaktiert", phone: "Telefonat", email: "E-Mail", meeting: "Gespräch", clarified: "Geklärt", jobcenter: "Jobcenter informiert", next_appointment: "Nächster Termin", other: "Sonstiges" };

export function WbAkteNotizen({ participantId }) {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("phone");
  const [notes, setNotes] = useState("");
  const load = () => api.get(`/weiterbildung/participants/${participantId}/interventions`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [participantId]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    if (!notes.trim()) { toast.error("Bitte eine Notiz eingeben"); return; }
    try { await api.post("/weiterbildung/interventions", { participant_id: participantId, kind, notes }); setNotes(""); toast.success("Notiz gespeichert"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-4" data-testid="wb-akte-notizen">
      <div className="border border-slate-200 bg-white p-4 flex flex-wrap gap-2 items-start">
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="border border-slate-200 px-2 py-2 text-sm bg-white" data-testid="wb-akte-note-kind">
          {Object.entries(KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Was wurde besprochen / vereinbart?" className="flex-1 min-w-[200px] border border-slate-200 px-2 py-1.5 text-sm" data-testid="wb-akte-note-text" />
        <button onClick={add} className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-700" data-testid="wb-akte-note-add"><Plus size={14} /> Hinzufügen</button>
      </div>
      {rows.map((r) => (
        <div key={r.intervention_id} className="border-l-2 border-slate-300 pl-4 py-1" data-testid={`wb-akte-note-${r.intervention_id}`}>
          <div className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString("de-DE")} · {KINDS[r.kind] || r.kind} · {r.by_name}</div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap">{r.notes}</div>
        </div>
      ))}
      {!rows.length && <div className="text-sm text-slate-500">Noch keine Notizen.</div>}
    </div>
  );
}
